// Import migrated Junto Pay API services
import {
  authService as realAuthService,
  classService as realClassService,
  referenceDataService,
  membershipService as realMembershipService,
  userService as realUserService,
  storeService as realStoreService,
  cartService as realPromoCodeService
} from './migratedJuntoAPI.js';

// Current user state (will be managed by real auth)
let currentUser = null;

// Authentication API - now using real Junto Pay API
export const authService = {
  async login(email, password) {
    try {
      const result = await realAuthService.login(email, password);
      currentUser = result.user;
      return result;
    } catch (error) {
      throw error;
    }
  },

  async signup(userData) {
    try {
      const result = await realAuthService.signup(userData);
      currentUser = result.user;
      return result;
    } catch (error) {
      throw error;
    }
  },

  async logout() {
    try {
      const result = await realAuthService.logout();
      currentUser = null;
      return result;
    } catch (error) {
      currentUser = null;
      throw error;
    }
  },

  async forgotPassword(email) {
    return await realAuthService.forgotPassword(email);
  },

  async getCurrentUser() {
    if (!currentUser) {
      currentUser = await realAuthService.getCurrentUser();
    }
    return currentUser;
  }
};

// Location API - now using real Junto Pay API
export const locationService = {
  async getLocations() {
    return await referenceDataService.getLocations();
  },

  async getLocationById(id) {
    const locations = await this.getLocations();
    return locations.find(loc => loc.id === id);
  },

  async getRooms() {
    return await referenceDataService.getRooms();
  }
};

// Class API - now using real Junto Pay API with parallel data loading
export const classService = {
  async getClasses(filters = {}) {
    return await realClassService.getClasses(filters);
  },

  async getClassById(id) {
    return await realClassService.getClassById(id);
  },

  async bookClass(classId, paymentMethod = 'credit') {
    if (!currentUser) {
      throw new Error('User must be logged in to book classes');
    }
    
    return await realClassService.bookClass(classId, paymentMethod);
  },

  async cancelBooking(bookingId) {
    return await realClassService.cancelBooking(bookingId);
  }
};

// Membership/Pricing API - now using real Junto Pay API with credit system
export const membershipService = {
  async getMemberships(filters = {}) {
    return await realMembershipService.getMemberships(filters);
  },

  async getMembershipById(id) {
    return await realMembershipService.getMembershipById(id);
  },

  async purchaseMembership(membershipId, paymentMethodId) {
    if (!currentUser) {
      throw new Error('User must be logged in to purchase memberships');
    }
    
    return await realMembershipService.purchaseMembership(membershipId, paymentMethodId);
  },

  // Credit packages (new functionality from real API)
  async getCreditPackages(filters = {}) {
    return await realMembershipService.getCreditPackages(filters);
  },

  async purchaseCreditPackage(packageId, paymentMethodId) {
    if (!currentUser) {
      throw new Error('User must be logged in to purchase credit packages');
    }
    
    return await realMembershipService.purchaseCreditPackage(packageId, paymentMethodId);
  }
};

// Credit Package API - extracted from membership service for cleaner imports
export const creditPackageService = {
  async getCreditPackages(filters = {}) {
    return await realMembershipService.getCreditPackages(filters);
  },

  async getCredits(filters = {}) {
    return await realMembershipService.getCredits(filters);
  },

  async getCategories(filters = {}) {
    return await realMembershipService.getCategories(filters);
  },
  
  async getPurchasableEntities(filters = {}) {
    return await realMembershipService.getPurchasableEntities(filters);
  },

  async purchaseCreditPackage(packageId, paymentMethodId) {
    if (!currentUser) {
      throw new Error('User must be logged in to purchase credit packages');
    }
    
    return await realMembershipService.purchaseCreditPackage(packageId, paymentMethodId);
  }
};

// User Account API - now using real Junto Pay API with wallet integration
export const userService = {
  async getUserProfile(userId) {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }
    
    return await realUserService.getUserProfile(userId);
  },

  async updateUserProfile(userId, updates) {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }
    
    const result = await realUserService.updateUserProfile(userId, updates);
    // Update local current user
    if (result.success) {
      currentUser = result.user;
    }
    return result;
  },

  async addPaymentMethod(paymentMethodData) {
    return await realUserService.addPaymentMethod(paymentMethodData);
  },

  async removePaymentMethod(paymentMethodId) {
    return await realUserService.removePaymentMethod(paymentMethodId);
  },

  async getUserBookings(userId, filters = {}) {
    return await realUserService.getUserBookings(userId, filters);
  },

  async getUserMemberships(userId) {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }
    
    return await realUserService.getUserMemberships(userId);
  },

  async getPurchaseHistory(userId) {
    return await realUserService.getPurchaseHistory(userId);
  },

  // New wallet functionality from real API
  async getUserWallet(userId) {
    return await realUserService.getUserWallet(userId);
  }
};

// Instructor API - now using real Junto Pay API with reference data
export const instructorService = {
  async getInstructors() {
    return await referenceDataService.getInstructors();
  },

  async getInstructorById(id) {
    const instructors = await this.getInstructors();
    return instructors.find(inst => inst.id === id);
  }
};

// Store API - now using real Junto Pay API
export const storeService = {
  async getStoreItems(filters = {}) {
    return await realStoreService.getStoreItems(filters);
  },

  async getStoreItemById(id) {
    return await realStoreService.getStoreItemById(id);
  },

  async purchaseStoreItem(itemId, quantity, paymentMethodId) {
    return await realStoreService.purchaseStoreItem(itemId, quantity, paymentMethodId);
  }
};

// Promo Code API - now using real Junto Pay API
export const promoCodeService = {
  async validatePromoCode(code, cartItems = []) {
    return await realPromoCodeService.validatePromoCode(code, cartItems);
  },

  async applyPromoCode(code, cartItems = []) {
    return await realPromoCodeService.applyPromoCode(code, cartItems);
  }
};

// Expose reference data service for components that need it
export { referenceDataService };